'use client';

import { Box, Divider, Typography, Checkbox, FormControlLabel } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { FC, useState, useMemo, useEffect, useRef, type SyntheticEvent, type ChangeEvent } from 'react';
import { OptionKey } from '@/lib/options/optionTypes';
import { useSelectOptions } from '@/lib/options/useSelectOptions';
import { log } from '@/lib/utils/logger';
import { nullIfBlank } from '@/lib/utils/nullIfBlank';
import { getProblemDetail } from '@/lib/utils/apiErrors';
import { FormAutocomplete, FormDatePicker } from '@/components/common/FormComponents';
import { FormLabel } from '@/components/common/FormLabel';
import { StandardDialog } from '@/components/common/StandardDialog';
import { useUserInteraction } from '@/hooks/useUnsavedChanges';
import { useBackendStore } from '@/stores/backendStore';
import {
  CollaboratorDto,
  CreateSampleActivityRequest,
  MaterialDto,
  SampleActivityDto,
  SampleDto,
  ActivityTypeDto,
  ActivityCategoryDto,
  SynthesisTechniqueDto,
  RecipeFavoriteDto,
} from '@/api/ApiClient';
import { useAuthStore } from '@/stores/authStore';
import { RichTextEditor } from '@/components/common/RichTextEditor';
import { useSnackbar } from '@/hooks/useSnackbar';
import { ACTIVITY_TYPE_LABELS, PROCESSING_ACTIVITY_TYPE_OPTIONS } from '@/lib/types/activityTypes';
import { MaterialCreateAddButton } from '@/components/material/MaterialCreateAddButton';
import { MaterialCreateDialog } from '@/components/material/MaterialCreateDialog';
import { useCreateMaterial } from '@/hooks/useCreateMaterial';
import { buildTechniqueOptions } from '@/lib/utils/optionUtils';
import { toNonSubstrateMaterialPicks } from '@/lib/materials/materialOptions';
import { filterAutocompleteOptionsByMatchRank } from '@/lib/utils/autocompleteMatchRank';
import { toRecipeTemplateStep } from '@/lib/utils/recipeTemplateStep';
import type { ProcessingDialogPrefill } from '@/stores/dialogStore';
import { validateRequiredToSave } from '@/lib/utils/saveValidation';
import { createProcessingActivitySaveValidation } from '@/lib/validation/saveValidationBuilders';
import { buildCreateRecipePayloadAsync, fetchRecipeFavoritesForProcessing } from '@/lib/utils/recipeFavoriteAtCreate';

const OPTION_KEYS: OptionKey[] = [
  'researcherOptions',
  'staff',
  'synthesisInstruments',
  'synthesisTechniquesLeafOnly',
  'activityType',
  'materials',
];

interface AddProcessingDialogProps {
  open: boolean;
  onClose: () => void;
  sample: SampleDto;
  /** When set (e.g. opening from Add to inventory), seeds date, researcher, technique, and description. */
  prefill?: ProcessingDialogPrefill | null;
  onSubmit?: (activity: SampleActivityDto) => void;
}

interface AddProcessingDialogInnerProps extends AddProcessingDialogProps {
  staffOptions: CollaboratorDto[];
  synthesisTechniquesLeafOnly: SynthesisTechniqueDto[];
  synthesisInstruments: { id?: string; name?: string; synthesisTechniques?: string[] }[];
  materialOptionsSource: MaterialDto[];
  initialDate: Date;
  initialTechnique: { id: string; name: string } | null;
  initialResearcher: CollaboratorDto | null;
  initialDescription: string;
}

type TechniquePick = { id: string; name: string };
type InstrumentPick = { id: string; name: string };
type ProcessingTypePick = { id: string; name: string };
type MaterialPick = { id: number; label: string };

type SynthesisInstrumentOption = {
  id?: string;
  name?: string;
  synthesisTechniques?: string[];
};

/**
 * When the researcher changes, technique/instrument should only clear if no longer allowed
 * (same rules as buildTechniqueOptions + instrument list for the current technique).
 */
function pruneTechniqueInstrumentForResearcher(
  nextResearcher: CollaboratorDto | null,
  currentTechnique: TechniquePick | null,
  currentInstrument: InstrumentPick | null,
  leafTechniques: SynthesisTechniqueDto[] | undefined,
  synthesisInstruments: SynthesisInstrumentOption[] | undefined
): { clearTechnique: boolean; clearInstrument: boolean } {
  const nextTechniqueOptions = buildTechniqueOptions(leafTechniques, nextResearcher?.techniques);
  const techniqueStillAllowed =
    !!currentTechnique?.id && nextTechniqueOptions.some((t) => String(t.id) === String(currentTechnique.id));

  if (!techniqueStillAllowed) {
    return { clearTechnique: true, clearInstrument: true };
  }

  const base = synthesisInstruments ?? [];
  const forTechnique = currentTechnique?.id
    ? base.filter((inst) => inst.synthesisTechniques?.includes(currentTechnique.id))
    : base;
  const allowedInstrumentIds = new Set(
    forTechnique.filter((m) => m.id != null && m.name != null && m.name !== 'None').map((m) => String(m.id!))
  );
  const instrumentStillAllowed = !!currentInstrument?.id && allowedInstrumentIds.has(String(currentInstrument.id));

  return { clearTechnique: false, clearInstrument: !instrumentStillAllowed };
}

const AddProcessingDialogInner: FC<AddProcessingDialogInnerProps> = ({
  open,
  onClose,
  sample,
  prefill: _prefill,
  onSubmit,
  staffOptions,
  synthesisTechniquesLeafOnly,
  synthesisInstruments,
  materialOptionsSource,
  initialDate,
  initialTechnique,
  initialResearcher,
  initialDescription,
}) => {
  const backend = useBackendStore((state) => state.getBackend());
  const { showSnackbar } = useSnackbar();
  const { canCreateMaterial } = useCreateMaterial();
  const [selectedDate, setSelectedDate] = useState<Date | null>(initialDate);
  const [selectedTechnique, setSelectedTechnique] = useState<{ id: string; name: string } | null>(initialTechnique);
  const [selectedInstrument, setSelectedInstrument] = useState<{ id: string; name: string } | null>(null);
  const [selectedProcessingType, setSelectedProcessingType] = useState<{ id: string; name: string } | null>(null);
  // Track user interaction - once user does anything, always show warning on close
  const { hasUnsavedChanges, markAsInteracted, reset } = useUserInteraction();
  const [selectedResearcher, setSelectedResearcher] = useState<CollaboratorDto | null>(initialResearcher);
  const [selectedMaterial, setSelectedMaterial] = useState<{ id: number; label: string } | null>(null);
  const [isMaterialDialogOpen, setIsMaterialDialogOpen] = useState(false);
  const [updatedMaterials, setUpdatedMaterials] = useState<{ id: number; label: string }[] | null>(null);
  const [availableTemplates, setAvailableTemplates] = useState<string[]>([]);
  const [selectedRecipeTemplate, setSelectedRecipeTemplate] = useState<string | null>(null);
  const [availableFavorites, setAvailableFavorites] = useState<RecipeFavoriteDto[]>([]);
  const [selectedFavorite, setSelectedFavorite] = useState<RecipeFavoriteDto | null>(null);
  const [loadingFavorites, setLoadingFavorites] = useState(false);
  const [enableNickname, setEnableNickname] = useState(false);
  const [description, setDescription] = useState(initialDescription);
  /** Prevents double-submit before React re-renders (e.g. slow dev / double click). */
  const submitInFlightRef = useRef(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const resetAllStates = () => {
    setSelectedDate(initialDate);
    setSelectedTechnique(initialTechnique);
    setSelectedInstrument(null);
    setSelectedProcessingType(null);
    setSelectedResearcher(initialResearcher);
    setSelectedMaterial(null);
    setUpdatedMaterials(null);
    setAvailableTemplates([]);
    setSelectedRecipeTemplate(null);
    setAvailableFavorites([]);
    setSelectedFavorite(null);
    setEnableNickname(false);
    setDescription(initialDescription);
    reset();
    onClose();
  };

  //Filtered staff with role
  const filteredStaff = useMemo(() => {
    return staffOptions;
  }, [staffOptions]);

  // updatedMaterials resets automatically when dialog remounts via key prop

  // Technique options (leaf-only; filter by researcher's allowed techniques when set)
  const techniqueOptions = useMemo(() => {
    const leaf = synthesisTechniquesLeafOnly ?? [];
    const allowedTechniqueIds = selectedResearcher?.techniques;
    return buildTechniqueOptions(leaf, allowedTechniqueIds);
  }, [synthesisTechniquesLeafOnly, selectedResearcher?.techniques]);

  // Instrument options
  const instrumentOptions = useMemo(() => {
    const base = synthesisInstruments ?? [];
    const filtered = selectedTechnique?.id
      ? base.filter((inst) => inst.synthesisTechniques?.includes(selectedTechnique.id))
      : base;
    return filtered
      .filter((m) => m.id !== undefined && m.name !== undefined && m.name !== 'None')
      .sort((a, b) => a.name!.localeCompare(b.name!))
      .map((m) => ({
        id: m.id!,
        name: m.name!,
      }));
  }, [synthesisInstruments, selectedTechnique]);

  const handleResearcherChange = (_: SyntheticEvent, newValue: CollaboratorDto | null) => {
    setSelectedResearcher(newValue);
    const { clearTechnique, clearInstrument } = pruneTechniqueInstrumentForResearcher(
      newValue,
      selectedTechnique,
      selectedInstrument,
      synthesisTechniquesLeafOnly,
      synthesisInstruments
    );
    if (clearTechnique) {
      setSelectedTechnique(null);
      setSelectedInstrument(null);
    } else if (clearInstrument) {
      setSelectedInstrument(null);
    }
    markAsInteracted();
  };

  const handleProcessingTypeChange = (_: SyntheticEvent, newValue: ProcessingTypePick | string | null) => {
    if (typeof newValue === 'string') {
      setSelectedProcessingType({ id: '', name: newValue });
    } else {
      setSelectedProcessingType(newValue);
      if (newValue?.id !== ActivityTypeDto.Synthesis) {
        setSelectedMaterial(null);
        setEnableNickname(false);
      }
    }
    setSelectedFavorite(null);
    markAsInteracted();
  };

  const handleDateChange = (newValue: Date | null) => {
    setSelectedDate(newValue);
    markAsInteracted();
  };

  const handleTechniqueChange = (_: SyntheticEvent, newValue: TechniquePick | string | null) => {
    if (typeof newValue === 'string') {
      setSelectedTechnique({ id: '', name: newValue });
    } else {
      setSelectedTechnique(newValue);
      setSelectedInstrument(null);
    }
    setSelectedFavorite(null);
    markAsInteracted();
  };

  const handleInstrumentChange = (_: SyntheticEvent, newValue: InstrumentPick | string | null) => {
    if (typeof newValue === 'string') {
      setSelectedInstrument({ id: '', name: newValue });
    } else {
      setSelectedInstrument(newValue);
    }
    setSelectedFavorite(null);
    markAsInteracted();
  };

  const handleRecipeTemplateChange = (_: SyntheticEvent, selectedName: string | null) => {
    setSelectedRecipeTemplate(selectedName);
    setSelectedFavorite(null);
    markAsInteracted();
  };

  const handleFavoriteChange = (_: SyntheticEvent, favorite: RecipeFavoriteDto | null) => {
    setSelectedFavorite(favorite);
    if (favorite?.template) {
      setSelectedRecipeTemplate(favorite.template);
    } else if (favorite?.recipe?.templateName) {
      setSelectedRecipeTemplate(favorite.recipe.templateName);
    }
    markAsInteracted();
  };

  const handleMaterialChange = (_: SyntheticEvent, newValue: MaterialPick | null) => {
    setSelectedMaterial(newValue);
    markAsInteracted();
  };

  const handleNicknameChange = (event: ChangeEvent<HTMLInputElement>) => {
    setEnableNickname(event.target.checked);
    markAsInteracted();
  };

  const handleDescriptionChange = (value: string) => {
    setDescription(value);
    markAsInteracted();
  };

  const handleOpenMaterialDialog = () => {
    setIsMaterialDialogOpen(true);
  };

  const handleCloseMaterialDialog = () => {
    setIsMaterialDialogOpen(false);
  };

  // Material options (non-substrates only) - use updated materials if available, otherwise use cached options
  const materialOptions = useMemo(() => {
    return (
      updatedMaterials ||
      (materialOptionsSource
        ?.filter((m) => m.id !== undefined && m.name !== undefined && m.name !== 'None' && !m.isSubstrate)
        .sort((a, b) => a.name!.localeCompare(b.name!))
        .map((m) => ({
          id: m.id!,
          label: m.name!,
        })) ??
        [])
    );
  }, [materialOptionsSource, updatedMaterials]);

  // Processing options
  const processingOptions = PROCESSING_ACTIVITY_TYPE_OPTIONS.map((id) => ({
    id,
    name: ACTIVITY_TYPE_LABELS[id],
  }));

  const isSynthesis = selectedProcessingType?.id === ActivityTypeDto.Synthesis;
  const techniqueHelperText =
    selectedProcessingType?.id === ActivityTypeDto.Synthesis
      ? 'Select the technique used for this synthesis run.'
      : selectedProcessingType?.id === ActivityTypeDto.Preparation
        ? 'Optional. Leave empty for generic substrate preparation, or choose a technique to load recipe templates specific to this technique.'
        : selectedProcessingType?.id
          ? 'Optional. Choose if this step used a specific technique.'
          : undefined;
  const instrumentHelperText =
    selectedProcessingType?.id === ActivityTypeDto.Synthesis
      ? 'Select the instrument used for this synthesis run.'
      : selectedProcessingType?.id === ActivityTypeDto.Preparation
        ? 'Optional. Leave empty for substrate preparation before loading, or choose an instrument if this prep step used one.'
        : selectedProcessingType?.id
          ? 'Optional. Choose if this step used a specific chamber or instrument.'
          : undefined;

  useEffect(() => {
    if (!open) {
      return;
    }
    const controller = new AbortController();
    const loadAvailableRecipeTemplates = async () => {
      const processingTypeId = selectedProcessingType?.id;

      if (!processingTypeId || (!selectedTechnique?.id && processingTypeId === ActivityTypeDto.Synthesis)) {
        if (!controller.signal.aborted) {
          setAvailableTemplates([]);
          setSelectedRecipeTemplate(null);
          setAvailableFavorites([]);
          setSelectedFavorite(null);
        }
        return;
      }

      const step = toRecipeTemplateStep(processingTypeId) ?? undefined;
      if (!step) {
        return;
      }

      try {
        const templates = await backend.recipeTemplate.availableTemplates(
          step,
          selectedTechnique?.id,
          selectedInstrument?.id
        );

        if (controller.signal.aborted) {
          return;
        }

        const names = templates.map((t) => t.name).filter((name): name is string => !!name);
        setAvailableTemplates(names);
        setSelectedRecipeTemplate(names.length > 0 ? names[0] : null);
        setSelectedFavorite(null);
      } catch (err) {
        if (controller.signal.aborted) {
          return;
        }
        log.error('[AddProcessingDialog] Failed to load recipe templates or full template:', err);
        setAvailableTemplates([]);
        setSelectedRecipeTemplate(null);
        setAvailableFavorites([]);
        setSelectedFavorite(null);
      }
    };
    void loadAvailableRecipeTemplates();
    return () => controller.abort();
  }, [open, backend, selectedProcessingType?.id, selectedTechnique?.id, selectedInstrument?.id]);

  useEffect(() => {
    if (!open) {
      return;
    }
    const controller = new AbortController();
    const loadFavorites = async () => {
      const processingTypeId = selectedProcessingType?.id;
      if (!processingTypeId || !selectedRecipeTemplate) {
        if (!controller.signal.aborted) {
          setAvailableFavorites([]);
        }
        return;
      }
      if (processingTypeId === ActivityTypeDto.Synthesis && !selectedTechnique?.id) {
        if (!controller.signal.aborted) {
          setAvailableFavorites([]);
        }
        return;
      }

      const step = toRecipeTemplateStep(processingTypeId);
      if (!step) {
        return;
      }

      setLoadingFavorites(true);
      try {
        const favorites = await fetchRecipeFavoritesForProcessing(
          backend,
          selectedTechnique?.id,
          selectedInstrument?.id,
          selectedRecipeTemplate,
          processingTypeId
        );
        if (!controller.signal.aborted) {
          setAvailableFavorites(favorites);
        }
      } catch (err) {
        if (!controller.signal.aborted) {
          log.error('[AddProcessingDialog] Failed to load recipe favorites:', err);
          setAvailableFavorites([]);
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoadingFavorites(false);
        }
      }
    };
    void loadFavorites();
    return () => controller.abort();
  }, [
    open,
    backend,
    selectedProcessingType?.id,
    selectedTechnique?.id,
    selectedInstrument?.id,
    selectedRecipeTemplate,
  ]);

  const handleSubmit = async () => {
    log.debug('[AddProcessingDialog] Submitting processing data:');

    if (!sample?.id) {
      showSnackbar('Please fill in all required fields', 'error');
      log.error('[AddProcessingDialog] Missing required fields');
      return;
    }

    const saveValidation = validateRequiredToSave(
      createProcessingActivitySaveValidation({
        userId: selectedResearcher?.id ? String(selectedResearcher.id) : undefined,
        date: selectedDate ?? undefined,
        type: selectedProcessingType?.id,
        instrument: nullIfBlank(selectedInstrument?.id),
      })
    );

    if (!saveValidation.isValid) {
      showSnackbar('Please fill in all required fields', 'error');
      log.error('[AddProcessingDialog] Missing required fields for processing activity');
      return;
    }

    if (submitInFlightRef.current) {
      return;
    }
    submitInFlightRef.current = true;
    setIsSubmitting(true);

    const recipePayload = await buildCreateRecipePayloadAsync(backend, selectedFavorite, selectedRecipeTemplate);
    if (selectedFavorite && !recipePayload.recipe) {
      showSnackbar('Could not load recipe from favorite. Try again or pick a template only.', 'error');
      submitInFlightRef.current = false;
      setIsSubmitting(false);
      return;
    }

    // Safe non-null assertions: saveValidation above guarantees researcher/date/type are present.
    const createActivityRequest: CreateSampleActivityRequest = {
      sampleId: sample.id,
      createNickname: enableNickname ?? false,
      userId: selectedResearcher!.id,
      date: selectedDate!,
      technique: nullIfBlank(selectedTechnique?.id),
      instrument: nullIfBlank(selectedInstrument?.id),
      desc: description || '',
      recipeTemplate: recipePayload.recipeTemplate,
      recipe: recipePayload.recipe,
      runtime: recipePayload.runtime,
      type: selectedProcessingType!.id as ActivityTypeDto,
      category: ActivityCategoryDto.Processing,
      materials: selectedMaterial
        ? [
            {
              id: selectedMaterial.id,
              name: selectedMaterial.label,
              asSampleMaterial: true, // This material is being synthesized
            },
          ]
        : undefined,
    };
    try {
      const created = await backend.sampleActivity.create(createActivityRequest);
      log.debug('[AddProcessingDialog] Created sample activity:', created);
      log.debug('[AddProcessingDialog] Alias:', created.sample?.refSampleLabel);

      onSubmit?.(created);
      onClose();
      showSnackbar(`Created new activity succesfully`, 'success');
    } catch (error) {
      log.error('[AddProcessingDialog] Failed to create sample activity:', error);
      showSnackbar(getProblemDetail(error) ?? 'Failed to create activity.', 'error');
    } finally {
      submitInFlightRef.current = false;
      setIsSubmitting(false);
    }
    resetAllStates();
  };

  return (
    <StandardDialog
      open={open}
      onClose={onClose}
      title={selectedProcessingType ? `Start ${selectedProcessingType.name}` : 'Start Processing'}
      maxWidth="md"
      onSubmit={handleSubmit}
      submitText="Start Processing"
      submitDisabled={
        !selectedResearcher ||
        !selectedDate ||
        !selectedProcessingType ||
        (selectedProcessingType.id === ActivityTypeDto.Synthesis && !selectedInstrument?.id)
      }
      isLoading={isSubmitting}
      hasUnsavedChanges={hasUnsavedChanges}
      unsavedChangesMessage="You have unsaved changes to this processing activity. Are you sure you want to discard them?"
      onConfirmClose={resetAllStates}
    >
      {/* Line 1: Processing Type (full width) */}
      <FormAutocomplete
        options={processingOptions}
        getOptionLabel={(option) => (typeof option === 'string' ? option : option.name)}
        value={selectedProcessingType}
        onChange={handleProcessingTypeChange}
        label="Processing Type"
        required
        sx={{ flex: 1 }}
      />
      {/* Line 2: User, Date */}
      <Box display="flex" alignItems="column" gap={2}>
        {/* User */}
        <Box flex={1}>
          <FormAutocomplete
            options={filteredStaff ?? []}
            getOptionLabel={(option) => option.fullName || ''}
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                {option.fullName}
              </li>
            )}
            value={selectedResearcher}
            onChange={handleResearcherChange}
            label="Who's Starting Activity:"
            required
            autoSelect
            autoHighlight
          />
        </Box>
        <Box flex={1}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <FormDatePicker label="Date" value={selectedDate} onChange={handleDateChange} required />
          </LocalizationProvider>
        </Box>
      </Box>
      {/* Divider after Row 2 */}
      <Divider />
      {/* Line 3: Technique, Instrument, Recipe (if exists) */}
      <Box display="flex" gap={2}>
        <FormAutocomplete
          options={techniqueOptions}
          getOptionLabel={(option) => (typeof option === 'string' ? option : option.name)}
          value={selectedTechnique}
          onChange={handleTechniqueChange}
          required={isSynthesis}
          helperText={techniqueHelperText}
          label="Technique"
          sx={{ flex: 1 }}
          autoSelect
          autoHighlight
        />
        <FormAutocomplete
          options={instrumentOptions}
          getOptionLabel={(option) => (typeof option === 'string' ? option : option.name)}
          value={selectedInstrument}
          onChange={handleInstrumentChange}
          label="Instrument"
          required={isSynthesis}
          disabled={!selectedTechnique?.id}
          helperText={instrumentHelperText}
          sx={{ flex: 1 }}
          autoSelect
          autoHighlight
        />
        {/* Recipe Template – always visible with contextual helper text */}
        <FormAutocomplete
          options={availableTemplates}
          value={selectedRecipeTemplate}
          inputValue={selectedRecipeTemplate ?? ''}
          getOptionLabel={(option) => String(option)}
          disabled={!selectedProcessingType?.id}
          helperText={!availableTemplates.length ? 'No recipe templates available' : undefined}
          onChange={handleRecipeTemplateChange}
          label="Recipe Template"
          sx={{ flex: 1 }}
          autoSelect
          autoHighlight
        />
      </Box>
      <Box display="flex" gap={2}>
        <FormAutocomplete
          options={availableFavorites}
          getOptionLabel={(option) => option.name || ''}
          value={selectedFavorite}
          onChange={handleFavoriteChange}
          label="Recipe Favorite"
          disabled={!selectedRecipeTemplate || loadingFavorites}
          helperText={
            loadingFavorites
              ? 'Loading favorites...'
              : !selectedRecipeTemplate
                ? 'Select a recipe template first'
                : availableFavorites.length === 0
                  ? 'No favorites for this template'
                  : 'Optional — pre-fill recipe from a saved favorite'
          }
          sx={{ flex: 1 }}
        />
      </Box>
      {/* Line 4: Material being synthesized - only show for Synthesis processing type */}
      {selectedProcessingType?.id === ActivityTypeDto.Synthesis ? (
        <Box display="flex" gap={1} sx={{ flex: 1 }}>
          <FormAutocomplete
            options={materialOptions}
            getOptionLabel={(option) => option.label}
            filterOptions={(opts, params) =>
              filterAutocompleteOptionsByMatchRank(opts, params, (o) => String(o.label ?? ''))
            }
            isOptionEqualToValue={(option, value) => option.id === value.id}
            value={selectedMaterial}
            onChange={handleMaterialChange}
            autoHighlight
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                {option.label}
              </li>
            )}
            label="Material being synthesized"
            sx={{ flex: 1 }}
          />
          {canCreateMaterial ? <MaterialCreateAddButton onClick={handleOpenMaterialDialog} /> : null}
        </Box>
      ) : null}
      {/* Line 5: Nickname - only show for Synthesis processing type with technique and instrument */}
      {selectedProcessingType?.id === ActivityTypeDto.Synthesis && selectedTechnique && selectedInstrument ? (
        <>
          <Divider />
          <FormControlLabel
            control={<Checkbox checked={enableNickname} onChange={handleNicknameChange} />}
            label="Create instrument-specific Sample Nickname"
          />
        </>
      ) : null}
      {/* Divider after Row 2 */}
      <Divider />
      {/* Line 5: Description */}
      <Box width="100%">
        <Typography variant="subtitle2" gutterBottom>
          <FormLabel optional>Notes</FormLabel>
        </Typography>
        <Box width="100%">
          <RichTextEditor value={description} onChange={handleDescriptionChange} />
        </Box>
      </Box>

      <MaterialCreateDialog
        open={isMaterialDialogOpen}
        forSubstrate={false}
        onClose={handleCloseMaterialDialog}
        onCreated={(savedMaterial, freshMaterials) => {
          const nextMaterialOptions = toNonSubstrateMaterialPicks(freshMaterials);
          setUpdatedMaterials(nextMaterialOptions);
          const createdMaterial = nextMaterialOptions.find((m) => m.id === savedMaterial.id);
          if (createdMaterial) {
            setSelectedMaterial(createdMaterial);
          }
          showSnackbar('New material created successfully', 'success');
        }}
      />
    </StandardDialog>
  );
};

const AddProcessingDialog: FC<AddProcessingDialogProps> = ({ open, onClose, sample, prefill, onSubmit }) => {
  const { options, loading: optionsLoading } = useSelectOptions(OPTION_KEYS);
  const { currentUser } = useAuthStore();

  const staffOptions = useMemo(
    () => options.staff?.filter((s) => s.roles?.some((role) => role.isResearcher)) ?? [],
    [options.staff]
  );
  const synthesisTechniquesLeafOnly = useMemo(
    () => options.synthesisTechniquesLeafOnly ?? [],
    [options.synthesisTechniquesLeafOnly]
  );
  const synthesisInstruments = useMemo(() => options.synthesisInstruments ?? [], [options.synthesisInstruments]);
  const materialOptionsSource = useMemo(() => options.materials ?? [], [options.materials]);

  const initialTechnique = ((): TechniquePick | null => {
    if (prefill?.technique?.id) return prefill.technique;
    const sampleTechniqueId = sample?.techniqueId;
    if (!sampleTechniqueId) return null;
    const fromOptions = synthesisTechniquesLeafOnly.find((t) => String(t.id) === String(sampleTechniqueId));
    return fromOptions
      ? { id: fromOptions.id!, name: fromOptions.name! }
      : { id: sampleTechniqueId, name: sampleTechniqueId };
  })();

  const initialResearcher = (() => {
    const resolvedResearcherUserId = prefill?.researcherUserId || sample?.userId || '';
    if (resolvedResearcherUserId.length > 0) {
      const fromPrefill = staffOptions.find((s) => String(s.id) === String(resolvedResearcherUserId));
      if (fromPrefill) return fromPrefill;
    }
    if (currentUser?.id == null) return null;
    return staffOptions.find((r) => r.id === currentUser.id) ?? null;
  })();

  const initialDate = prefill?.date != null ? new Date(prefill.date) : new Date();
  const initialDescription = prefill?.description ?? '';

  const dialogInitKey = `${sample?.id ?? 'sample'}|${prefill?.researcherUserId ?? ''}|${prefill?.technique?.id ?? ''}|${
    prefill?.date ?? ''
  }|${currentUser?.id ?? 'anon'}|${currentUser?.selectedRole ?? ''}|${String(currentUser?.usingSignInAs ?? false)}`;

  const waitingMessage = !sample ? 'No sample selected.' : optionsLoading ? 'Loading options...' : null;
  if (waitingMessage) {
    return (
      <StandardDialog
        open={open}
        onClose={onClose}
        title="Start Processing"
        maxWidth="md"
        onSubmit={() => {}}
        submitText="Start Processing"
        submitDisabled
      >
        <Box sx={{ py: 2 }}>{waitingMessage}</Box>
      </StandardDialog>
    );
  }

  return (
    <AddProcessingDialogInner
      key={dialogInitKey}
      open={open}
      onClose={onClose}
      sample={sample}
      prefill={prefill}
      onSubmit={onSubmit}
      staffOptions={staffOptions}
      synthesisTechniquesLeafOnly={synthesisTechniquesLeafOnly}
      synthesisInstruments={synthesisInstruments}
      materialOptionsSource={materialOptionsSource}
      initialDate={initialDate}
      initialTechnique={initialTechnique}
      initialResearcher={initialResearcher}
      initialDescription={initialDescription}
    />
  );
};

export { AddProcessingDialog };
